// Renderer + scene + camera + lights. Mirrors the tanks/zombies World shape:
// one class owns the WebGLRenderer, Scene, PerspectiveCamera, a follow sun, and resize.

import * as THREE from "three";
import { GradePass } from "./grade";
import { SKY_COLOR, FOG_COLOR, CAMERA } from "../config";

/** Objects put on this layer ALSO render into the selective-bloom pass (genuine light bleed, e.g. the Blaster's
 *  neon red). Enable it with `mesh.layers.enable(BLOOM_LAYER)`; the main pass is unaffected. */
export const BLOOM_LAYER = 1;

/** Multi-mesh SECTOR renderOrder bands (perf profiler): every opaque LEAF of a sector is tagged with its band so
 *  its draws form ONE contiguous run in the opaque list (the sort collapses to renderOrder since no Group sets a
 *  nonzero one). NEVER set a Group's renderOrder inside a banded subtree — it ejects the leaves into another
 *  bucket and splits the run. Bands are clear of the existing leaf orders (-1 terrain/skirt/grass, 1 water, 10 sky, 998/999 healthbars). */
export const FACTORY_BAND = 20;
export const UNITS_BAND = 30;

export class Scene {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  private sun: THREE.DirectionalLight;
  private ambient: THREE.AmbientLight;
  private hemi: THREE.HemisphereLight;
  private grid: THREE.GridHelper;
  // sun direction (light → target), shared by the shadow sun AND the atmosphere bake so sky + shadows agree
  private sunDir = new THREE.Vector3(1200, 2600, 1600).normalize();
  private sunDist = 3283;
  private sunOffset = new THREE.Vector3();
  // Per-SECTOR GPU cost via EXT_disjoint_timer_query_webgl2. One timer query, rotated across channels each
  // frame (never nested), so we measure the terrain / sky / whole-frame draw times DIRECTLY (not inferred).
  private gl2: WebGL2RenderingContext | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private timerExt: any = null;
  costs: Record<string, number> = { total: -1, bloom: -1, shadow: -1, grade: -1 }; // channel name → last GPU ms (-1 = n/a)
  private channels: string[] = ["total", "bloom", "shadow", "grade"]; // bloom = the overlay pass; shadow = the shadow-map pass; grade = the post pass
  /** The single post-processing pass (sharpen / LUT / outline / CRT / grain). Owns the RT the scene renders
   *  into — everything downstream of the main draw composites there, and `grade.run()` is the only thing that
   *  touches the default framebuffer. Disable it and the scene draws straight to screen exactly as before. */
  grade!: GradePass;
  private extraChannels: string[] = []; // sector-band + reflection channels that own no probe object
  private _sentGeo: THREE.BufferGeometry | null = null;
  private _sentMat: THREE.MeshBasicMaterial | null = null;
  private probes = new Map<string, THREE.Object3D>();
  private cycle = 0;
  private q: WebGLQuery | null = null; private qName = ""; private qBegan = false; private qEnded = false;
  private inFlight: WebGLQuery | null = null; private inFlightName = "";
  renderScale = 1; // current canvas backing-store pixel ratio (dynamic resolution drives this)

  // Selective bloom: only objects on BLOOM_LAYER get a genuine blurred light-bleed, composited additively over the
  // frame. Off until something opts in (bloomActive), so no-bloom battles pay nothing.
  bloomActive = false;
  /** Additive passes drawn after the composite (see the tail of render()) — e.g. the factory's ore lens flares. */
  readonly overlayPasses: Array<(r: THREE.WebGLRenderer) => void> = [];
  // Heavy DYNAMIC geometry (skinned units, grass) to SKIP in the cheap re-render passes (bloom occluder + water
  // reflection) — re-transforming/re-skinning them there costs a lot but they barely occlude/reflect. Set by main.ts.
  heavyExclude: THREE.Object3D[] = [];
  // Skipped in the WATER REFLECTION ONLY (heavyExclude is skipped in the bloom occluder too). The factory goes
  // here and not there: it is the single biggest draw-call block in the scene, but its buildings are exactly the
  // geometry that must OCCLUDE the ore glow in the bloom pass — dropping it from that would let ore bloom bleed
  // through solid machines. A reflection at quarter res is the one pass that can afford to lose it.
  reflectExclude: THREE.Object3D[] = [];
  private bloomA!: THREE.WebGLRenderTarget; // has depth: occluders write it so the neon glow is hidden behind geometry
  private bloomB!: THREE.WebGLRenderTarget;
  private blackMat!: THREE.MeshBasicMaterial; // occluder override: draws all geometry black (+depth), cheap fragment
  private blurMat!: THREE.ShaderMaterial;
  private addMat!: THREE.ShaderMaterial;
  private quadScene!: THREE.Scene;
  private quadCam!: THREE.OrthographicCamera;
  private quad!: THREE.Mesh;

  constructor(container: HTMLElement) {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(SKY_COLOR);
    this.scene.fog = new THREE.Fog(FOG_COLOR, 3000, 14000);

    this.camera = new THREE.PerspectiveCamera(
      CAMERA.fov,
      window.innerWidth / window.innerHeight,
      CAMERA.near,
      CAMERA.far,
    );

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    // The render scale (canvas backing-store pixel ratio) is the single biggest GPU lever — fragment cost
    // (procedural terrain, cloud raymarch, ACES, shadow taps) scales linearly with it. It's driven live by
    // dynamic resolution; start at the display DPR capped at 1.5.
    this.renderScale = Math.min(window.devicePixelRatio, 1.5);
    this.renderer.setPixelRatio(this.renderScale);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap; // fewer taps/fragment than PCFSoft (the shadow pass re-renders every caster each frame)
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.3;
    container.appendChild(this.renderer.domElement);
    const gl = this.renderer.getContext();
    if (gl instanceof WebGL2RenderingContext) {
      this.gl2 = gl;
      this.timerExt = gl.getExtension("EXT_disjoint_timer_query_webgl2");
    }
    this.setupBloom();
    this.grade = new GradePass(this.renderer);
    // shadow-pass timer: three fires scene.onBeforeRender immediately before its shadow-map pass. On the "shadow"
    // turn, begin the timer here; it's closed at the first main-pass color draw (in registerCostProbe's onBeforeRender).
    this.scene.onBeforeRender = (): void => {
      const g2 = this.gl2, ex = this.timerExt;
      if (g2 && ex && this.qName === "shadow" && this.q && !this.qBegan) { g2.beginQuery(ex.TIME_ELAPSED_EXT, this.q); this.qBegan = true; }
    };

    // lights: fill + sky/ground + a shadow-casting sun
    this.ambient = new THREE.AmbientLight(0xffffff, 0.72);
    this.scene.add(this.ambient);
    this.hemi = new THREE.HemisphereLight(0x9fb8d8, 0x2a2e36, 0.85);
    this.scene.add(this.hemi);
    this.sun = new THREE.DirectionalLight(0xfff1d6, 1.7);
    this.sunOffset.copy(this.sunDir).multiplyScalar(this.sunDist);
    this.sun.position.copy(this.sunOffset);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(1536, 1536); // ~44% fewer depth fragments than 2048² at ~4 studs/texel — still crisp
    const sc = this.sun.shadow.camera;
    sc.left = -3000;
    sc.right = 3000;
    sc.top = 3000;
    sc.bottom = -3000;
    sc.near = 100;
    sc.far = 9000;
    this.sun.shadow.bias = -0.0004;
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);

    // faint reference grid on the ground plane
    this.grid = new THREE.GridHelper(40000, 400, 0x2a3038, 0x1b2027);
    (this.grid.material as THREE.Material).transparent = true;
    (this.grid.material as THREE.Material).opacity = 0.35;
    this.scene.add(this.grid);

    window.addEventListener("resize", this.onResize);
  }

  get domElement(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  /** Keep the shadow frustum centered on a focus point so shadows stay crisp. */
  focusSun(x: number, z: number): void {
    this.sun.position.set(x + this.sunOffset.x, this.sunOffset.y, z + this.sunOffset.z);
    this.sun.target.position.set(x, 0, z);
    // no manual updateMatrixWorld: renderer.render()'s scene.updateMatrixWorld() refreshes the target's
    // matrix from this identical position before the shadow pass reads it (scene.matrixWorldAutoUpdate stays true).
  }

  /** Aim the sun (light → target direction). The atmosphere bakes its sky with the SAME vector so the
   *  sun's position in the sky matches the direction shadows are cast. Campaign-only; sandbox keeps the
   *  default. Returns the normalized direction for the caller to feed the scattering bake. */
  setSunDir(dir: THREE.Vector3): THREE.Vector3 {
    this.sunDir.copy(dir).normalize();
    this.sunOffset.copy(this.sunDir).multiplyScalar(this.sunDist);
    return this.sunDir;
  }

  setGridVisible(v: boolean): void {
    this.grid.visible = v;
  }

  /** Terra battles: smooth sloped ground under grazing sun needs a NORMAL bias (≈1 texel at
   *  ~3.9 studs/texel) or slope-acne stripes appear; the tile decks' flat faces never needed it. */
  setShadowSoftGround(): void {
    this.sun.shadow.normalBias = 4;
  }

  /** Retint the scene's air + lighting for a planet biome (fog, sun, ambient, hemisphere, exposure). */
  applyBiome(cfg: {
    fog: number; fogNear: number; fogFar: number;
    sunColor: number; sunIntensity: number;
    ambient: number; ambientI: number;
    hemiSky: number; hemiGround: number; hemiI: number;
    exposure: number;
  }): void {
    this.scene.fog = new THREE.Fog(cfg.fog, cfg.fogNear, cfg.fogFar);
    this.scene.background = new THREE.Color(cfg.fog); // horizon fallback behind the sky dome
    this.sun.color.setHex(cfg.sunColor);
    this.sun.intensity = cfg.sunIntensity;
    this.ambient.color.setHex(cfg.ambient);
    this.ambient.intensity = cfg.ambientI;
    this.hemi.color.setHex(cfg.hemiSky);
    this.hemi.groundColor.setHex(cfg.hemiGround);
    this.hemi.intensity = cfg.hemiI;
    this.renderer.toneMappingExposure = cfg.exposure;
  }

  add(obj: THREE.Object3D): void { this.scene.add(obj); }

  /** Set the 3D render scale = the canvas backing-store pixel ratio. ONLY the WebGL buffer resizes; three's
   *  setPixelRatio re-applies setSize with updateStyle=false, so the canvas DISPLAY size (and every DOM UI
   *  element, which is separate) is untouched. That's the clean way to do dynamic resolution / supersampling
   *  without the browser-zoom flaw of scaling the interface. */
  setRenderScale(scale: number): void {
    this.renderScale = scale;
    this.renderer.setPixelRatio(scale);
    // The post RT must track the backing store — a stale size would leave the grade pass sampling a buffer
    // that no longer matches the frame, and `uTexel` (sharpen/outline tap spacing) would be wrong with it.
    const ds = this.renderer.getDrawingBufferSize(new THREE.Vector2());
    this.grade?.setSize(ds.x, ds.y);
    this.bloomA?.setSize(Math.max(1, ds.x >> 1), Math.max(1, ds.y >> 1));
    this.bloomB?.setSize(Math.max(1, ds.x >> 1), Math.max(1, ds.y >> 1));
  }

  /** Where the frame is being assembled: the grade RT while post is on, else the default framebuffer. Bloom
   *  and the overlay passes composite HERE, not to the screen, or post would grade a frame missing them. */
  private frameTarget(): THREE.WebGLRenderTarget | null {
    return this.grade && this.grade.enabled ? this.grade.target : null;
  }

  private onResize = (): void => {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    const ds = this.renderer.getDrawingBufferSize(new THREE.Vector2());
    this.bloomA.setSize(Math.max(1, ds.x >> 1), Math.max(1, ds.y >> 1));
    this.bloomB.setSize(Math.max(1, ds.x >> 1), Math.max(1, ds.y >> 1));
    this.grade?.setSize(ds.x, ds.y);
  };

  /** Register an object as its own GPU-cost "sector" (times just its main-pass draw via onBeforeRender/
   *  onAfterRender). Re-register the same name to point at a rebuilt object (e.g. terrain on regenerate). */
  registerCostProbe(name: string, obj: THREE.Object3D): void {
    const gl = this.gl2, ext = this.timerExt;
    if (!gl || !ext) return;
    if (!(name in this.costs)) this.costs[name] = -1;
    this.probes.set(name, obj);
    this.rebuildChannels();
    const isShadow = (m?: THREE.Material): boolean => !!m && (m.type === "MeshDepthMaterial" || m.type === "MeshDistanceMaterial");
    obj.onBeforeRender = (_r, _s, _c, _g, material): void => {
      if (isShadow(material)) return; // ignore the shadow-map depth pass — we want the color-shading cost
      // first MAIN-pass color draw → the shadow map is complete; close the "shadow" timer (begun in scene.onBeforeRender)
      if (this.qName === "shadow" && this.qBegan && !this.qEnded && this.q) { gl.endQuery(ext.TIME_ELAPSED_EXT); this.qEnded = true; }
      if (this.q && this.qName === name && !this.qBegan) { gl.beginQuery(ext.TIME_ELAPSED_EXT, this.q); this.qBegan = true; }
    };
    obj.onAfterRender = (_r, _s, _c, _g, material): void => {
      if (isShadow(material)) return;
      if (this.qBegan && this.qName === name && !this.qEnded) { gl.endQuery(ext.TIME_ELAPSED_EXT); this.qEnded = true; }
    };
  }

  private rebuildChannels(): void {
    this.channels = ["total", "bloom", "shadow", ...this.probes.keys(), ...this.extraChannels];
  }

  /** Bracket a MULTI-mesh SECTOR ("band"): the caller tags renderOrder===band on every opaque leaf of the sector so
   *  those draws form one contiguous run; two degenerate sentinel draws at band∓0.5 begin/end the timer for `name`.
   *  `band` must be unique and nothing else may use [band−0.5, band+0.5]. Sentinels are added to the scene ONCE. */
  registerCostBand(name: string, band: number): void {
    const gl = this.gl2, ext = this.timerExt;
    if (!gl || !ext) return;
    if (!(name in this.costs)) this.costs[name] = -1;
    if (!this.extraChannels.includes(name)) { this.extraChannels.push(name); this.rebuildChannels(); }
    if (!this._sentGeo) {
      this._sentGeo = new THREE.BufferGeometry();
      this._sentGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(9), 3)); // degenerate tri → 0 fragments
      this._sentGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 0);
    }
    if (!this._sentMat) this._sentMat = new THREE.MeshBasicMaterial({ colorWrite: false, depthTest: false, depthWrite: false });
    const isShadow = (m?: THREE.Material): boolean => !!m && (m.type === "MeshDepthMaterial" || m.type === "MeshDistanceMaterial");
    const mk = (ro: number, begin: boolean): THREE.Mesh => {
      const s = new THREE.Mesh(this._sentGeo!, this._sentMat!);
      s.renderOrder = ro; s.frustumCulled = false; // always listed → bracket stays balanced even when the sector is fully culled (reads ~0)
      s.castShadow = false; s.receiveShadow = false; s.matrixAutoUpdate = false;
      s.onBeforeRender = (_r, _s2, camera, _g, material): void => {
        if (camera !== this.camera) return;  // ignore the water-reflection / any nested-camera re-render
        if (isShadow(material)) return;       // ignore the shadow depth pass
        if (this.qName === "shadow" && this.qBegan && !this.qEnded && this.q) { gl.endQuery(ext.TIME_ELAPSED_EXT); this.qEnded = true; } // redundant shadow-close (harmless)
        if (this.qName !== name || !this.q) return;
        if (begin) { if (!this.qBegan) { gl.beginQuery(ext.TIME_ELAPSED_EXT, this.q); this.qBegan = true; } }
        else if (this.qBegan && !this.qEnded) { gl.endQuery(ext.TIME_ELAPSED_EXT); this.qEnded = true; }
      };
      return s;
    };
    this.scene.add(mk(band - 0.5, true), mk(band + 0.5, false));
  }

  /** Register the water planar-reflection nested render as its own round-robin channel. */
  registerReflectionChannel(): void {
    if (!("reflection" in this.costs)) this.costs.reflection = -1;
    if (!this.extraChannels.includes("reflection")) { this.extraChannels.push("reflection"); this.rebuildChannels(); }
  }
  /** Bracket the water reflection's nested renderer.render(). No-op unless it's the reflection turn; step 4 finalizes it. */
  reflectionBegin(): void {
    const gl = this.gl2, ext = this.timerExt;
    if (gl && ext && this.qName === "reflection" && this.q && !this.qBegan) { gl.beginQuery(ext.TIME_ELAPSED_EXT, this.q); this.qBegan = true; }
  }
  reflectionEnd(): void {
    const gl = this.gl2, ext = this.timerExt;
    if (gl && ext && this.qName === "reflection" && this.qBegan && !this.qEnded) { gl.endQuery(ext.TIME_ELAPSED_EXT); this.qEnded = true; }
  }

  render(): void {
    const gl = this.gl2, ext = this.timerExt;
    // 1. read a completed query into its channel
    if (gl && ext && this.inFlight && gl.getQueryParameter(this.inFlight, gl.QUERY_RESULT_AVAILABLE)) {
      if (!gl.getParameter(ext.GPU_DISJOINT_EXT)) this.costs[this.inFlightName] = gl.getQueryParameter(this.inFlight, gl.QUERY_RESULT) / 1e6;
      gl.deleteQuery(this.inFlight); this.inFlight = null;
    }
    // 2. pick this frame's channel. Only "total" begins here; "shadow" begins in scene.onBeforeRender, object
    //    probes in their own onBeforeRender, and "bloom" is bracketed around renderBloom() in step 5.
    this.q = null; this.qName = ""; this.qBegan = false; this.qEnded = false;
    if (gl && ext && !this.inFlight) {
      this.qName = this.channels[this.cycle % this.channels.length]; this.cycle++;
      this.q = gl.createQuery();
      if (this.q && this.qName === "total") { gl.beginQuery(ext.TIME_ELAPSED_EXT, this.q); this.qBegan = true; }
    }
    // 3. render — probe / shadow callbacks fire here. Into the post RT when the grade pass is on, so bloom and
    //    the overlay flares below composite into the SAME buffer and get graded with everything else.
    const ft = this.frameTarget();
    this.renderer.setRenderTarget(ft);
    this.renderer.render(this.scene, this.camera);
    // 4. finalize a scene-object PROBE or the SHADOW query (both begin AND end inside the render above)
    if (gl && ext && this.q && this.qName !== "total" && this.qName !== "bloom") {
      if (this.qEnded) { this.inFlight = this.q; this.inFlightName = this.qName; }
      else { if (this.qBegan) gl.endQuery(ext.TIME_ELAPSED_EXT); gl.deleteQuery(this.q); } // culled (or shadow never closed) → close if open, discard
      this.q = null;
    }
    // 5. selective-bloom overlay — metered so it lands in "total" (on the total turn) AND its own "bloom" channel
    if (this.qName === "bloom") {
      if (gl && ext && this.q && this.bloomActive) { // time JUST renderBloom() on the bloom turn
        gl.beginQuery(ext.TIME_ELAPSED_EXT, this.q); this.renderBloom(); gl.endQuery(ext.TIME_ELAPSED_EXT);
        this.inFlight = this.q; this.inFlightName = "bloom"; this.q = null;
      } else { // bloom off → nothing to time
        if (gl && ext && this.q) { gl.deleteQuery(this.q); this.q = null; }
        this.costs.bloom = 0; this.renderBloom(); // no-op while bloomActive is false
      }
    } else {
      // On the "total" turn this runs inside total's still-open bracket. The bracket is NOT closed here: the
      // overlay flares and the post pass still have to happen, and closing early is how bloom once ended up
      // outside "total" entirely (PERF.md). Total closes at the very end of the frame, below.
      this.renderBloom();
    }
    // Additive overlays, drawn LAST over the fully composited (bloomed) frame — the same slot the campaign
    // gives its star flares, which is what makes a flare read as light in the lens rather than a decal in
    // the world. Empty by default, so a battle with nothing to flare pays nothing.
    this.renderer.setRenderTarget(ft);            // bloom may have left another target bound
    for (const pass of this.overlayPasses) pass(this.renderer);

    // 6. POST — the one pass that touches the default framebuffer. Metered on its own channel so its cost is
    //    measured rather than assumed (it lands inside "total" on the total turn, as bloom does).
    if (ft) {
      this.grade.setCamera(this.camera.near, this.camera.far);
      if (gl && ext && this.q && this.qName === "grade") {
        gl.beginQuery(ext.TIME_ELAPSED_EXT, this.q);
        this.grade.run(performance.now() / 1000, this.renderScale);
        gl.endQuery(ext.TIME_ELAPSED_EXT);
        this.inFlight = this.q; this.inFlightName = "grade"; this.q = null;
      } else {
        this.grade.run(performance.now() / 1000, this.renderScale);
      }
    } else if (gl && ext && this.q && this.qName === "grade") {
      gl.deleteQuery(this.q); this.q = null; this.costs.grade = 0;   // post off → nothing to time
    }
    // Close "total" only NOW — after main, bloom, overlays and post — so the channel means the whole frame
    // rather than the part of it that happened before the last thing anyone added.
    if (gl && ext && this.q && this.qName === "total" && this.qBegan) {
      gl.endQuery(ext.TIME_ELAPSED_EXT); this.qEnded = true;
      this.inFlight = this.q; this.inFlightName = "total"; this.q = null;
    }
  }

  private setupBloom(): void {
    const ds = this.renderer.getDrawingBufferSize(new THREE.Vector2());
    this.bloomA = new THREE.WebGLRenderTarget(Math.max(1, ds.x >> 1), Math.max(1, ds.y >> 1), { depthBuffer: true, stencilBuffer: false });
    this.bloomB = new THREE.WebGLRenderTarget(Math.max(1, ds.x >> 1), Math.max(1, ds.y >> 1), { depthBuffer: false, stencilBuffer: false });
    this.blackMat = new THREE.MeshBasicMaterial({ color: 0x000000 });
    const vert = "varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }";
    this.blurMat = new THREE.ShaderMaterial({
      uniforms: { uTex: { value: null }, uDir: { value: new THREE.Vector2() } },
      vertexShader: vert,
      fragmentShader:
        "uniform sampler2D uTex; uniform vec2 uDir; varying vec2 vUv;" +
        "void main(){ vec4 c = texture2D(uTex, vUv)*0.227027" +
        " + texture2D(uTex, vUv+uDir*1.3846)*0.316216 + texture2D(uTex, vUv-uDir*1.3846)*0.316216" +
        " + texture2D(uTex, vUv+uDir*3.2308)*0.070270 + texture2D(uTex, vUv-uDir*3.2308)*0.070270;" +
        " gl_FragColor = c; }",
      depthTest: false, depthWrite: false,
    });
    this.addMat = new THREE.ShaderMaterial({
      uniforms: { uTex: { value: null }, uStrength: { value: 1.35 } },
      vertexShader: vert,
      fragmentShader: "uniform sampler2D uTex; uniform float uStrength; varying vec2 vUv; void main(){ gl_FragColor = texture2D(uTex, vUv) * uStrength; }",
      transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false,
    });
    this.quadScene = new THREE.Scene();
    this.quadCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.blurMat);
    this.quad.frustumCulled = false;
    this.quadScene.add(this.quad);
  }

  /** Selective bloom: render just the BLOOM_LAYER objects (the neon) to a half-res target, blur it, and add it back
   *  over the frame — a genuine light bleed limited to those objects. Everything else in the battle is untouched. */
  private renderBloom(): void {
    if (!this.bloomActive) return;
    const r = this.renderer, cam = this.camera, sc = this.scene;
    const sMask = cam.layers.mask, sBg = sc.background, sFog = sc.fog, sShadow = r.shadowMap.autoUpdate, sAuto = r.autoClear, sOv = sc.overrideMaterial;
    sc.background = null; sc.fog = null; r.shadowMap.autoUpdate = false; r.autoClear = false;
    r.setRenderTarget(this.bloomA);
    r.setClearColor(0x000000, 1); r.clear(); // clear color + depth once (autoClear off so the two passes share it)
    // PASS 1: whole scene as BLACK (writing DEPTH) so foreground geometry OCCLUDES the neon behind it. Terrain +
    //   buildings bake their positions, so a basic-material override lands them at the right depth.
    sc.overrideMaterial = this.blackMat;
    const ex = this.heavyExclude, exVis = ex.map((o) => o.visible);
    for (const o of ex) o.visible = false; // skinned units + grass barely occlude the glow but re-skinning them here is costly → skip
    r.render(sc, cam);
    for (let i = 0; i < ex.length; i++) ex[i].visible = exVis[i];
    // PASS 2: the neon only, real material, depth-tested against pass 1 → it shows ONLY where nothing's in front.
    sc.overrideMaterial = null; cam.layers.set(BLOOM_LAYER);
    r.render(sc, cam);
    cam.layers.mask = sMask; sc.overrideMaterial = sOv; sc.background = sBg; sc.fog = sFog; r.shadowMap.autoUpdate = sShadow;
    // 2. separable Gaussian blur, ping-pong ×2 → a soft wide bleed
    const w = this.bloomA.width, h = this.bloomA.height, dir = this.blurMat.uniforms.uDir.value as THREE.Vector2;
    this.quad.material = this.blurMat;
    for (let i = 0; i < 2; i++) {
      this.blurMat.uniforms.uTex.value = this.bloomA.texture; dir.set(1.3 / w, 0);
      r.setRenderTarget(this.bloomB); r.render(this.quadScene, this.quadCam);
      this.blurMat.uniforms.uTex.value = this.bloomB.texture; dir.set(0, 1.3 / h);
      r.setRenderTarget(this.bloomA); r.render(this.quadScene, this.quadCam);
    }
    // 3. additive composite over the frame
    this.quad.material = this.addMat; this.addMat.uniforms.uTex.value = this.bloomA.texture;
    r.setRenderTarget(this.frameTarget()); r.autoClear = false;   // the frame, not the screen — post still to come
    r.render(this.quadScene, this.quadCam);
    r.autoClear = sAuto;
  }
}
